import React, { useEffect, useState } from 'react'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop'
import { Button, Chip, Portal, Theme } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { COMMIT_HASH, IS_DEMO, IS_PENTEST, IS_PROD, IS_STAGING } from '~config'

import { environmentIndicatorEvents } from '../events/environmentIndicatorEvents'

interface EnvironmentIndicatorProps {
  position?: 'top-right' | 'bottom-right' | 'bottom-left'
  renderIndicator?: boolean
}

const positionStyles = {
  'top-right': {
    top: (theme: Theme): string => theme.spacing(2),
    right: (theme: Theme): string => theme.spacing(2),
  },
  'bottom-right': {
    bottom: (theme: Theme): string => theme.spacing(2),
    right: (theme: Theme): string => theme.spacing(2),
  },
  'bottom-left': {
    bottom: (theme: Theme): string => theme.spacing(2),
    left: (theme: Theme): string => theme.spacing(2),
  },
}

export const CentralEnvironmentIndicator: React.FC = () => {
  const [position, setPosition] = useState<'top-right' | 'bottom-right' | 'bottom-left'>(
    'top-right',
  )
  const [isLocalDb, setIsLocalDb] = useState(false)

  useEffect(() => {
    const unsubscribe = environmentIndicatorEvents.subscribe(setPosition)
    return unsubscribe
  }, [])

  if (IS_PROD) return null

  const getLocalDbCheck = async (): Promise<void> => {
    if (IS_STAGING || IS_DEMO || IS_PROD || IS_PENTEST) return
    const response = await apiZodios.api_app_localDBCheck({})
    setIsLocalDb(response.using_local_db)
  }

  useEffect(() => {
    getLocalDbCheck()
  }, [])

  const handleCommitClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    window.open(`https://github.com/polimorphic/poli-mono/commit/${COMMIT_HASH}`, '_blank')
  }

  return (
    <Portal>
      <Chip
        label={
          IS_DEMO || IS_PENTEST ? (
            IS_PENTEST ? (
              'PENTEST ENVIRONMENT'
            ) : (
              'DEMO ENVIRONMENT'
            )
          ) : IS_STAGING ? (
            <React.Fragment>
              STAGING ENVIRONMENT:{' '}
              <Button
                onClick={handleCommitClick}
                variant='text'
                size='small'
                title={`View commit ${COMMIT_HASH} on GitHub`}
                sx={{ color: 'white' }}
              >
                {COMMIT_HASH}
              </Button>
            </React.Fragment>
          ) : (
            'LOCAL ENVIRONMENT'
          )
        }
        icon={
          !IS_DEMO && !IS_STAGING ? (
            isLocalDb ? (
              <InstallDesktopIcon color='inherit' fontSize='medium' />
            ) : (
              <CloudUploadIcon color='inherit' fontSize='medium' />
            )
          ) : undefined
        }
        size='medium'
        sx={{
          position: 'fixed',
          ...positionStyles[position],
          backgroundColor: (theme: Theme) =>
            IS_DEMO ? 'pink' : IS_STAGING ? theme.palette.success.main : theme.palette.primary.main,
          color: (theme: Theme) => theme.palette.warning.contrastText,
        }}
      />
    </Portal>
  )
}

// Use EnvironmentIndicator to set the position of the CentralEnvironmentIndicator component
const EnvironmentIndicator: React.FC<EnvironmentIndicatorProps> = ({
  position = 'top-right',
  renderIndicator = false,
}) => {
  useEffect(() => {
    environmentIndicatorEvents.setPosition(position)
  }, [position])

  if (renderIndicator) {
    return <CentralEnvironmentIndicator />
  }

  return null
}

export default EnvironmentIndicator
